import { Component, Input } from '@angular/core';
import { CommonModule } from '@angular/common';

/**
 * Wizard chrome for the space-claim page.
 *
 * The dark #004F6C bar this replaces was the single most off-brand element on
 * the page: an organizer arriving from the invitation met a colour that appears
 * nowhere on the site they had just been promised. Per aurora-tokens.css, the
 * bar is gone in favour of the site's nav lockup on cream, with progress
 * expressed as a coral rule plus a tag.
 *
 * The X mark is built from two rotated bars rather than an image so it inherits
 * the brand colours directly; the old shield PNG is pre-rebrand artwork.
 */
@Component({
  selector: 'app-space-claim-header',
  standalone: true,
  imports: [CommonModule],
  template: `
    <header class="claim-header">
      <!--
        The lockup is the way out of the wizard for someone who arrived from a
        cold invitation and wants to know what NX2U is before committing. A logo
        that goes nowhere is a dead end on the one page where the reader is most
        likely to have that question.

        NX2U in caps because the app draws the name rather than loading it
        (AuroraWordmark) and fixes the case in code, so every surface spells it
        the same way.
      -->
      <a class="lockup" href="https://nx2u.app/" target="_blank" rel="noopener"
         aria-label="NX2U — read more about NX2U on our website">
        <span class="xmark" aria-hidden="true"><i class="p1"></i><i class="p2"></i></span>
        <span class="wordmark">NX2U</span>
      </a>

      <h1 class="claim-title">{{ title }}</h1>

      <div class="progress-row" *ngIf="currentStep">
        <span class="au-tag">{{ currentStep }}</span>
        <div class="au-progress" [attr.aria-label]="currentStep">
          <i [style.width.%]="progressPercent"></i>
        </div>
      </div>
    </header>
  `,
  styleUrls: ['./space-claim-header.component.scss']
})
export class SpaceClaimHeaderComponent {
  @Input() title: string = 'Claim your event';
  /** Free text such as "Step 1 of 2"; drives both the tag and the rule. */
  @Input() currentStep: string | null = null;

  /* Parsed from the label so the rule and the words can never disagree. */
  get progressPercent(): number {
    const match = /(\d+)\s*of\s*(\d+)/i.exec(this.currentStep ?? '');
    if (!match) return 50;
    const [, step, total] = match;
    const value = (Number(step) / Number(total)) * 100;
    return Number.isFinite(value) ? Math.min(100, Math.max(0, value)) : 50;
  }
}
